/**
 * MainLayout Component - AWLP Design System
 * Main application layout with header, sidebar, and content area.
 * Mounts the EnvironmentBanner (when env var is set) and ImpersonationBanner
 * at the top of the layout stack, and the In-App Support widget launcher
 * (authenticated routes only — #497).
 */

import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Header } from '../Header';
import { Sidebar } from '../Sidebar';
import { EnvironmentBanner } from '../EnvironmentBanner';
import { ImpersonationBanner } from '../ImpersonationBanner';
import { PageErrorBoundary } from '../PageErrorBoundary';
import { SupportWidget, SUPPORT_LAUNCHER_SIZE_PX } from '../../support/SupportWidget';
import { useAuthStore } from '../../../stores/authStore';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.backgrounds.page};
`;

const Main = styled.div`
  display: flex;
  flex: 1;
`;

const Content = styled.main`
  flex: 1;
  padding: ${({ theme }) => theme.layout.content.padding};
  width: 100%;
  min-width: 0;
  /* Clearance for the fixed support launcher (bottom-right): page-bottom
     content — pagination chevrons in particular (#537) — must never end up
     underneath it. Launcher offset + bubble + breathing room. */
  padding-bottom: ${({ theme }) =>
    `calc(${theme.spacing[6]} + ${SUPPORT_LAUNCHER_SIZE_PX}px + ${theme.spacing[4]})`};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing[4]};
    padding-bottom: ${({ theme }) =>
      `calc(${theme.spacing[4]} + ${SUPPORT_LAUNCHER_SIZE_PX}px + ${theme.spacing[4]})`};
  }
`;

export const MainLayout = () => {
  const impersonation = useAuthStore((s) => s.impersonation);
  const location = useLocation();

  // Fire a best-effort end-impersonation call when the tab is closed or navigated away
  // while an impersonation session is active. keepalive ensures the request completes
  // even as the page unloads. Note: Safari has partial keepalive support — acceptable
  // trade-off given that the server also enforces the 15-minute TTL.
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Read fresh state inside the handler so we always have the latest value.
      const currentImpersonation = useAuthStore.getState().impersonation;
      if (!currentImpersonation) return;

      fetch('/api/v1/platform/impersonation/end', {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentImpersonation.token}`,
        },
        body: JSON.stringify({ sessionId: currentImpersonation.sessionId }),
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // Effect has no deps — the handler always reads from the store directly,
    // so there is no stale-closure risk and we only register/deregister once.
  }, []);

  return (
    <Container>
      <EnvironmentBanner />
      {impersonation && <ImpersonationBanner />}
      <Header />
      <Main>
        <Sidebar />
        <Content>
          {/* Keyed on the route so navigating away from a crashed page mounts a
              fresh boundary instead of staying stuck on the fallback. */}
          <PageErrorBoundary key={location.pathname}>
            <Outlet />
          </PageErrorBoundary>
        </Content>
      </Main>
      <SupportWidget />
    </Container>
  );
};

export default MainLayout;
